import { Component, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormControlFeedbackComponent, LanguageService, Loader, LoaderSpinnerComponent, ObjectPropertyNamesHolder, StoreLoader, UnsubscriberClass, ValidationService, ValidationServiceHelper } from '@aston/foundation';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AppConstants } from 'apps/console/src/app/app.constants';
import { AppStoreActions, AppStoreSelectors } from 'apps/console/src/app/root-store/app-store';
import { SubsidiaryDetailStoreActions, SubsidiaryDetailStoreSelectors } from 'apps/console/src/app/root-store/subsidiary-detail-store';
import { ConsoleDomainValidators } from 'apps/console/src/app/shared-module/enums/validators';
import { IUpdateSubsidiaryValidator } from 'apps/console/src/app/shared-module/enums/validators/update-subsidiary.validators';
import { SalesConditionsDocument } from 'apps/console/src/app/shared-module/models';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { NgxCurrencyDirective } from 'ngx-currency';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';

import { ISubsidiaryDetail, ISubsidiaryForm } from '../../models';
import { FileUploadComponent } from '../../../shared-module/components/file-upload/file-upload.component';
import { FileUploadInstructionsComponent } from '../../../shared-module/components/file-upload-instructions/file-upload-instructions.component';

export const formControlNames = (name: keyof ISubsidiaryForm) => name;

export type FormValidatorFieldsType = IUpdateSubsidiaryValidator['fields'];

@Component({
	selector: 'subsidiary-detail-company-page',
	templateUrl: './subsidiary-detail-company-page.component.html',
	imports: [
		AsyncPipe,
		TranslateModule,
		ReactiveFormsModule,
		NgxCurrencyDirective,
		NgbTooltipModule,
		LoaderSpinnerComponent,
		NgSelectModule,
		FormControlFeedbackComponent,
		FileUploadComponent,
		FileUploadInstructionsComponent,
	]
})
export class SubsidiaryDetailCompanyPageComponent extends UnsubscriberClass implements OnInit {
	private store = inject(Store);
	private formBuilder = inject(UntypedFormBuilder);
	private validationService = inject(ValidationService);
	private languageService = inject(LanguageService);
	private route = inject(ActivatedRoute);

	subsidiaryId: number;

	form: UntypedFormGroup;
	hadPreviousLogo: boolean;
	currentUserIsSupport: boolean;
	salesConditionsDocument: SalesConditionsDocument;
	formControlNames: ObjectPropertyNamesHolder<ISubsidiaryForm>;
	fileConfig = AppConstants.FILE_UPLOAD_CONFIGS.TENANT_LOGO;
	logoUrl: string;
	validationHelper: ValidationServiceHelper;
	rulesetFields: FormValidatorFieldsType;

	salesConditions$ = this.store.select(AppStoreSelectors.selectSalesConditionsDocument);

	isEditing$ = this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryFormIsEditing).pipe(
		tap(isEditing => this.setFormEditing(isEditing)),
		takeUntil(this.destroySubscriptions$)
	);

	currentSubsidiary$ = this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiary).pipe(
		filter(subsidiary => !!subsidiary),
		tap(subsidiary => this.setFormValues(subsidiary)),
		takeUntil(this.destroySubscriptions$)
	);

	currentSubsidiaryLoader = new StoreLoader(
		this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryIsLoading),
		this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryError)
	);

	subsidiaryFormLoader = new StoreLoader(
		this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryFormIsLoading),
		this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryFormError)
	);

	get legalIdentifierMask() {
		return this.isFrance ? AppConstants.SIRET_MASK : null;
	}

	currencySymbol$ = new BehaviorSubject('');

	currencies$ = this.languageService.language$.pipe(
		switchMap(lang => this.store.select(AppStoreSelectors.selectCurrencies(lang))),
		map(currencies => currencies.map(c => ({ ...c, currencyName: `${c.currencyCode} - ${c.currencyName}` }))),
	);

	countries$ = this.languageService.language$.pipe(
		switchMap(lang => this.store.select(AppStoreSelectors.selectCountries(lang)))
	);

	ngOnInit() {
		this.subsidiaryId = +this.route.parent.snapshot.params.id;

		this.form = this.formBuilder.group({});
		this.form.addControl(formControlNames('legalIdentifier'), new UntypedFormControl());
		this.form.addControl(formControlNames('name'), new UntypedFormControl());
		this.form.addControl(formControlNames('zipCode'), new UntypedFormControl());
		this.form.addControl(formControlNames('street'), new UntypedFormControl());
		this.form.addControl(formControlNames('city'), new UntypedFormControl());
		this.form.addControl(formControlNames('countryCode'), new UntypedFormControl());
		this.form.addControl(formControlNames('currencyCode'), new UntypedFormControl());
		this.form.addControl(formControlNames('annualFigure'), new UntypedFormControl());
		this.form.addControl(formControlNames('logo'), this.createFileUploaderFormGroup());
		this.form.addControl(formControlNames('isLogoDeleted'), new UntypedFormControl());
		this.form.addControl(formControlNames('salesConditionsAccepted'), new UntypedFormControl());

		this.validationService.getHelper(ConsoleDomainValidators.UpdateSubsidiaryValidator)
			.pipe(tap(([helper, fields]) => {
				this.validationHelper = helper;
				this.rulesetFields = fields as FormValidatorFieldsType;
				this.setFormValidators();
			}))
			.pipe(takeUntil(this.destroySubscriptions$))
			.subscribe();

		this.store.select(AppStoreSelectors.selectCurrentUserIsSupport)
			.pipe(takeUntil(this.destroySubscriptions$))
			.subscribe(isSupport => this.currentUserIsSupport = isSupport);

		// Load sales conditions document ID
		combineLatest([this.currentSubsidiary$, this.salesConditions$ ]).pipe(
			filter(([s, d]) => !!(s && d)),
			takeUntil(this.destroySubscriptions$)
		).subscribe(([subsidiary, document]) => {
			this.salesConditionsDocument = {
				...document,
				id: subsidiary.cgvDocumentId || document.documentId
			}
		});

		this.loadSubsidiary();

		this.isEditing$.subscribe();
		this.currentSubsidiary$.subscribe();
	}
	cancel() {
		this.logoField.get("fileInfo").reset();
		this.logoField.get("underlyingFile").reset();
		this.isLogoDeletedField.setValue(false);
		this.loadSubsidiary();
	}

	loadSubsidiary() {
		this.store.dispatch(SubsidiaryDetailStoreActions.LoadSubsidiaryRequest(this.subsidiaryId));
	}

	get legalIdentifierField() {
		return this.form.get(formControlNames('legalIdentifier'));
	}

	get nameField() {
		return this.form.get(formControlNames('name'));
	}

	get zipCodeField() {
		return this.form.get(formControlNames('zipCode'));
	}

	get streetField() {
		return this.form.get(formControlNames('street'));
	}

	get cityField() {
		return this.form.get(formControlNames('city'));
	}

	get countryCodeField() {
		return this.form.get(formControlNames('countryCode'));
	}

	get currencyCodeField() {
		return this.form.get(formControlNames('currencyCode'));
	}

	get annualFigureField() {
		return this.form.get(formControlNames('annualFigure'));
	}

	get logoField(): UntypedFormGroup {
		return this.form.get(formControlNames('logo')) as UntypedFormGroup;
	}

	get isLogoDeletedField() {
		return this.form.get(formControlNames('isLogoDeleted'));
	}

	get salesConditionsAcceptedField() {
		return this.form.get(formControlNames('salesConditionsAccepted'));
	}

	get isFrance() {
		const arrayFranceCountryCode = ["FR", "GF", "GP", "MQ", "YT", "RE", "BL", "MF", "PM"];
		return arrayFranceCountryCode.includes(this.countryCodeField.value);
	}

	createFileUploaderFormGroup() {
		return this.formBuilder.group({
			fileInfo: this.formBuilder.control(null),
			underlyingFile: this.formBuilder.control(null),
			fileLoader: this.formBuilder.control(new Loader())
		});
	}

	setFormValidators() {
		this.validationHelper.control(this.nameField, this.rulesetFields.Name);

		// Physical Address validators are not send by backend
		this.validationHelper.control(this.zipCodeField, this.rulesetFields.ZipCode);
		this.validationHelper.control(this.streetField, this.rulesetFields.Street);
		this.validationHelper.control(this.cityField, this.rulesetFields.City);
		this.validationHelper.control(this.countryCodeField, this.rulesetFields.Country);
		this.validationHelper.control(this.currencyCodeField, this.rulesetFields.CurrencyCode);

		this.validationHelper.control(this.annualFigureField, this.rulesetFields.AnnualFigure);
	}

	setFormValues(subsidiary: ISubsidiaryDetail) {
		this.logoField.get("fileInfo").reset();
		this.logoField.get("underlyingFile").reset();
		this.isLogoDeletedField.setValue(false);
		this.logoUrl = subsidiary.logoUrl;
		this.hadPreviousLogo = !!subsidiary.logoUrl;
		this.form.patchValue(subsidiary);

		// Sales conditions should be required for non admins only
		this.salesConditionsAcceptedField.clearValidators();
		if (!this.currentUserIsSupport) {
			this.salesConditionsAcceptedField.setValidators([Validators.requiredTrue]);
		}
		this.salesConditionsAcceptedField.updateValueAndValidity()
	}

	setFormEditing(isEditing: boolean) {
		if (isEditing) {
			this.nameField.enable();
			this.zipCodeField.enable();
			this.streetField.enable();
			this.cityField.enable();
			this.annualFigureField.enable();
			this.logoField.enable();
			if (!this.currentUserIsSupport && !this.salesConditionsAcceptedField.value) {
				this.salesConditionsAcceptedField.enable();
			}
		} else {
			this.nameField.disable();
			this.zipCodeField.disable();
			this.streetField.disable();
			this.cityField.disable();
			this.annualFigureField.disable();
			this.logoField.disable();
			this.salesConditionsAcceptedField.disable();
		}
	}

	getModelFromForm(): ISubsidiaryForm {
		return {
			name: this.nameField.value,
			logo: this.logoField.value,
			street: this.streetField.value,
			zipCode: this.zipCodeField.value,
			city: this.cityField.value,
			countryCode: this.countryCodeField.value,
			annualFigure: this.annualFigureField.value,
			isLogoDeleted: this.isLogoDeletedField.value && this.hadPreviousLogo,
			salesConditionsAccepted: !!this.salesConditionsAcceptedField.value,
			cgvDocumentId: this.salesConditionsAcceptedField.value ? this.salesConditionsDocument.documentId : null,
		};
	}

	onToggleEditing() {
		this.store.dispatch(SubsidiaryDetailStoreActions.ToggleIsEditingSubsidiary());
	}

	onSubmit() {
		this.store.dispatch(SubsidiaryDetailStoreActions.UpdateSubsidiaryRequest(this.getModelFromForm()));
	}

	openSalesConditions(event: Event) {
		event.preventDefault();
		this.store.dispatch(AppStoreActions.PreviewDocument({
			document: this.salesConditionsDocument,
			title: 'PageTitles.SalesConditions'
		}))
	}
}
